import React, { Fragment, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { ButtonC } from 'Common/Buttons';
import InputC from 'Common/Inputs/index.tsx';
import Axios from 'functions/Axios';
import close from '../../assets/V2-images/close.svg';
import { QR_CODE_URL } from 'components/Dam/DAM_API/urls';
import QrPreview from './QrPreview';
import QrTagsInput, { normaliseTags } from './QrTagsInput';
import { DAM_SVG } from 'components/Dam/DamAssets';
import {
  downloadQrCode,
  getQrTargets,
  isDeviceQr,
  QR_DOWNLOAD_FORMATS,
  QR_TYPE_OPTIONS,
  QR_TYPES,
  resolveCreatedQrCode,
} from './qrHelpers';

const emptyForm = {
  destinationUrl: '',
  androidUrl: '',
  iosUrl: '',
  title: '',
  qrType: QR_TYPES.STANDARD,
  tags: [],
};

/**
 * Three modes:
 *  - create  : both fields open, used by the QR Codes list page
 *  - edit    : `editData` is passed, both fields open, sends qrCodeId
 *  - locked  : `lockedDestinationUrl` is passed (asset embed URL), only the title is editable
 *
 * Independently of the mode, a code is either STANDARD (one destinationUrl) or DEVICE
 * (an Android and/or an iOS URL). The locked asset flow is always STANDARD.
 */
const QrFormModal = ({
  isOpen,
  toggle,
  editData = null,
  onSaved,
  lockedDestinationUrl = '',
  defaultTitle = '',
}) => {
  const isEditMode = Boolean(editData?.qrCodeId);
  const isLocked = Boolean(lockedDestinationUrl) && !isEditMode;
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState({});
  const [saving, setSaving] = useState(false);
  // Holds the freshly created code so the generated QR can be shown without a reload
  const [createdCode, setCreatedCode] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [tagsTouched, setTagsTouched] = useState(false);

  const isDeviceType = form.qrType === QR_TYPES.DEVICE;
  // Whether the row being edited told us its tags. If it did not, an untouched tag field
  // must not be sent, or saving would replace the code's real tags with an empty list.
  const tagsKnown = Array.isArray(editData?.qrTags) || Array.isArray(editData?.tags);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      destinationUrl: editData?.destinationUrl ?? lockedDestinationUrl ?? '',
      androidUrl: editData?.androidUrl ?? '',
      iosUrl: editData?.iosUrl ?? '',
      title: editData?.title ?? defaultTitle ?? '',
      // isDeviceQr, not editData.qrType: the list response does not reliably echo
      // qrType, so the saved type is inferred from the URLs the code carries.
      qrType: isDeviceQr(editData) ? QR_TYPES.DEVICE : QR_TYPES.STANDARD,
      tags: normaliseTags(editData?.qrTags ?? editData?.tags),
    });
    setTagsTouched(false);
    setError({});
    setCreatedCode(null);
    setUrlCopied(false);
  }, [isOpen, editData?.qrCodeId, lockedDestinationUrl, defaultTitle]);

  const clearError = (...names) =>
    setError((prev) => {
      const next = { ...prev };
      names.forEach((name) => delete next[name]);
      return next;
    });

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // the device URLs share one error, so clear that alongside the field's own
    if (error[name] || error.deviceUrls) {
      clearError(name, 'deviceUrls');
    }
  };

  /** The two types are exclusive. Typed values are kept so switching back is not lossy. */
  const handleTypeChange = (qrType) => {
    if (qrType === form.qrType) return;
    setForm((prev) => ({ ...prev, qrType }));
    clearError('destinationUrl', 'androidUrl', 'iosUrl', 'deviceUrls');
  };

  const validate = () => {
    const errors = {};

    if (isDeviceType) {
      if (!form.androidUrl?.trim() && !form.iosUrl?.trim()) {
        errors.deviceUrls = 'Enter at least one of the Android or iOS URLs';
      }
    } else if (!form.destinationUrl?.trim()) {
      errors.destinationUrl = 'Destination URL is required';
    }

    if (!form.title?.trim()) {
      errors.title = 'Title is required';
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(form.destinationUrl || '');
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      const body = {
        title: form.title.trim(),
        qrType: form.qrType,
        ...((!isEditMode || tagsKnown || tagsTouched) && { qrTags: form.tags }),
        ...(isDeviceType
          ? {
              // the backend picks between these per the scanning device
              destinationUrl: '',
              androidUrl: form.androidUrl.trim(),
              iosUrl: form.iosUrl.trim(),
            }
          : { destinationUrl: form.destinationUrl.trim() }),
        ...(isEditMode && { qrCodeId: editData.qrCodeId }),
      };
      const { data } = isEditMode
        ? await Axios.put(QR_CODE_URL, body)
        : await Axios.post(QR_CODE_URL, body);

      if (data?.success) {
        toast.success(data?.message ?? (isEditMode ? 'QR code updated' : 'QR code created'));
        onSaved && onSaved();

        const savedCode = resolveCreatedQrCode(data, body);
        if (!isEditMode && savedCode) {
          // Keep the modal open on a freshly generated code so it can be downloaded right away
          setCreatedCode(savedCode);
          setQrPreview(data?.data?.qrPreview);
        } else {
          toggle();
        }
      } else {
        toast.error(data?.message ?? 'Unable to save the QR code');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message ?? 'Unable to save the QR code');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (format) => {
    try {
      setDownloading(true);
      await downloadQrCode(createdCode, format);
    } catch (err) {
      console.error(err);
      toast.error('Unable to download the QR code');
    } finally {
      setDownloading(false);
    }
  };

  const closeBtn = (
    <img
      src={close}
      alt="closeicon"
      className="close"
      onClick={toggle}
      style={{ cursor: 'pointer' }}
    />
  );

  const modalTitle = () => {
    if (createdCode) return 'QR code created';
    if (isEditMode) return 'Edit QR code';
    if (isLocked) return 'Generate QR code';
    return 'Create a new QR code';
  };

  const renderLockedUrl = () => (
    <div>
      <label className="custom-label">Destination URL</label>
      {/* Not .Custom-input here: it carries width:100% + flex-shrink:0, which pushes
          the copy button out of the dialog once the URL is long. */}
      <div className="qr_locked_url_row">
        <input
          type="text"
          className="qr_locked_url_input"
          value={form.destinationUrl}
          title={form.destinationUrl}
          readOnly
        />
        <button type="button" className="qr_locked_url_copy" onClick={handleCopyUrl}>
          {urlCopied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );

  const renderForm = () => (
    <div className="qr_form_body">
      {isEditMode && (
        <div className="qr_form_preview_pane">
          {/* <QrPreview item={editData} size={160} /> */}
          <div className="qr_code_preview_box" style={{ width: 200, height: 200, minWidth: 200 }}>
            <div className="qr_code_preview_svg">
              <img src={editData?.qrPreview} alt="QR Code" style={{ objectFit: 'contain' }} />
            </div>
          </div>
          <span className="qr_code_meta_text">Current code</span>
        </div>
      )}

      <div className="qr_form_fields">
        {/* The asset flow is pinned to one fixed URL, so it gets no type switch */}
        {!isLocked && (
          <div className="qr_type_switch mb-3">
            {QR_TYPE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`qr_type_switch_btn ${form.qrType === option.key ? 'is_active' : ''}`}
                onClick={() => handleTypeChange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {isLocked && renderLockedUrl()}

        {!isLocked && !isDeviceType && (
          <InputC
            type="text"
            label="Destination URL"
            name="destinationUrl"
            required
            placeholder="https://example.com/my-long-url"
            inputvalue={form.destinationUrl}
            change={handleChange}
            error={error.destinationUrl}
          />
        )}

        {!isLocked && isDeviceType && (
          <Fragment>
            <InputC
              type="text"
              label="Android URL"
              name="androidUrl"
              placeholder="https://example.com/android"
              inputvalue={form.androidUrl}
              change={handleChange}
            />
            <div className="mt-3">
              <InputC
                type="text"
                label="iOS URL"
                name="iosUrl"
                placeholder="https://example.com/ios"
                inputvalue={form.iosUrl}
                change={handleChange}
              />
            </div>
            {error.deviceUrls && (
              <span style={{ color: 'red', fontSize: '14px' }}>{error.deviceUrls}</span>
            )}
          </Fragment>
        )}
        <div className="mt-3">
          <InputC
            type="text"
            label="Title"
            name="title"
            required
            placeholder="Enter a title for this code"
            inputvalue={form.title}
            change={handleChange}
            error={error.title}
          />
        </div>
        <div className="mt-3">
          <QrTagsInput
            value={form.tags}
            onChange={(tags) => {
              setForm((prev) => ({ ...prev, tags }));
              setTagsTouched(true);
            }}
          />
        </div>
        <div className="qr_form_hint mt-3">
          {isLocked &&
            'Scanning this code opens the asset URL above. The URL is fixed to this asset, only the title can be changed.'}
          {!isLocked &&
            isDeviceType &&
            'One code is generated for both. Android devices are sent to the Android URL and iOS devices to the iOS URL; fill in just one to send only that platform anywhere.'}
          {!isLocked &&
            !isDeviceType &&
            'Scanning this code sends the visitor to the destination URL. Editing the URL later keeps the same printed code working.'}
        </div>
      </div>
    </div>
  );

  const renderCreated = () => (
    <div className="d-flex flex-column align-items-center gap-3 py-2">
      {/* <QrPreview item={createdCode} size={200} /> */}
          <div className="qr_code_preview_box" style={{ width: 200, height: 200, minWidth: 200 }}>
            <div className="qr_code_preview_svg">
              <img src={qrPreview} alt="QR Code" style={{ objectFit: 'contain' }} />
            </div>
          </div>
      <div className="qr_code_card_title text-center">{createdCode?.title}</div>
      <div className="qr_code_meta_text text-center qr_code_created_url">
        {getQrTargets(createdCode).map((target) => (
          <div key={target.label || 'destination'}>
            {target.label ? `${target.label}: ` : ''}
            {target.url}
          </div>
        ))}
      </div>
      <div className="d-flex gap-2 flex-wrap justify-content-center">
        {QR_DOWNLOAD_FORMATS.map((format) => (
          <div
            key={format.key}
            className="d-flex align-items-center gap-2 justify-content-center pointer Secondary-Button large-button"
            onClick={() => !downloading && handleDownload(format.key)}
          >
            {DAM_SVG.inheritDownloadIcons}
            <div style={{ color: '#753CB7' }}>{format.short}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Fragment>
      <Modal isOpen={isOpen} toggle={toggle} className="qr_form_modal" style={{ width: '50%' }}>
        <ModalHeader toggle={toggle} close={closeBtn}>
          {modalTitle()}
        </ModalHeader>
        <ModalBody>{createdCode ? renderCreated() : renderForm()}</ModalBody>
        <ModalFooter style={{ borderTop: 'none' }}>
          {createdCode ? (
            <ButtonC text="Done" size="lg" click={toggle} />
          ) : (
            <Fragment>
              <ButtonC text="Cancel" color="Secondary" size="lg" click={toggle} />
              <ButtonC color="Primary" size="lg" disabled={saving} click={handleSave}>
                {saving && <Spinner color="light" className="mx-1" size="sm" />}
                {isEditMode ? 'Update' : isLocked ? 'Generate QR' : 'Create code'}
              </ButtonC>
            </Fragment>
          )}
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default QrFormModal;
